/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Box,
  Chip,
  ClickAwayListener,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';
import { dateUtils } from '@opengeoweb/shared';
import { ChevronDown, ChevronUp, Close } from '@opengeoweb/theme';
import type {
  ViewPresetListItem,
  WorkspacePresetListItem,
} from '../../store/workspace/types';
import { useWorkspaceTranslation } from '../../utils/i18n';
import { humanizeLabelStrings } from '../../utils/utils';

interface WorkspacePresetInfoProps {
  preset?: WorkspacePresetListItem | ViewPresetListItem;
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
  disablePortal?: boolean;
}

const formatDate = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const parsedDate =
    dateUtils.isoStringToDate(value, false) ??
    dateUtils.stringToDate(value, 'yyyy-MM-dd', false);

  if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return dateUtils.dateToString(parsedDate, 'dd/MM/yyyy', false) ?? value;
};

const WorkspacePresetInfo: React.FC<WorkspacePresetInfoProps> = ({
  preset,
  anchorEl,
  isOpen,
  onClose,
  disablePortal = false,
}) => {
  const { t } = useWorkspaceTranslation();
  const [isAbstractOpen, setIsAbstractOpen] = React.useState(false);
  const [isLabelsOpen, setIsLabelsOpen] = React.useState(false);
  const labelEntries = React.useMemo(
    () =>
      Object.entries(preset?.labels ?? {}).filter(
        ([, values]) => Array.isArray(values) && values.length > 0,
      ),
    [preset?.labels],
  );
  const totalLabelCount = React.useMemo(
    () =>
      labelEntries.reduce((sum, [, values]) => sum + (values?.length ?? 0), 0),
    [labelEntries],
  );

  const shortDescription = preset?.description ?? '';
  const abstractText = preset?.abstract ?? '';

  const labelFor = React.useCallback(
    (group: string, value: string): string => {
      const translationKey = `workspace-labels-${group}-${value}`;
      const translated = t(translationKey as string);
      if (translated !== translationKey) {
        return translated;
      }
      return humanizeLabelStrings(value);
    },
    [t],
  );

  React.useEffect(() => {
    if (!isOpen) {
      setIsAbstractOpen(false);
      setIsLabelsOpen(false);
    }
  }, [isOpen]);

  if (!preset) {
    return null;
  }

  return (
    <Popper
      anchorEl={anchorEl}
      container={anchorEl ? anchorEl.ownerDocument?.body : undefined}
      data-testid="workspace-preset-info-popper"
      disablePortal={disablePortal}
      modifiers={[
        {
          name: 'offset',
          options: { offset: [8, 0] },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: [
              'bottom-start',
              'top-start',
              'right-start',
              'left-start',
            ],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: 8,
            altBoundary: true,
            tether: true,
            boundary: 'viewport',
            altAxis: true,
          },
        },
      ]}
      open={isOpen}
      placement="auto"
      popperOptions={{
        strategy: 'fixed',
      }}
      sx={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={8}
          sx={{
            width: { xs: '100%', sm: 340 },
            maxWidth: { xs: 'calc(100vw - 16px)', sm: 340 },
            maxHeight: 'calc(100vh - 16px)',
            overflowY: 'auto',
            borderRadius: 2,
            boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.2)',
            padding: 2,
            bgcolor: 'geowebColors.background.surface',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Typography fontSize={20} fontWeight={400} variant="subtitle1">
              {t('workspace-info-title')}
            </Typography>
            <IconButton
              data-testid="workspace-preset-info-close"
              onClick={onClose}
              size="small"
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Box alignItems="center" display="flex" sx={{ mb: 1.75 }}>
            <Typography
              fontSize={16}
              fontWeight={400}
              gutterBottom
              sx={{ mr: 0.5 }}
              variant="subtitle2"
            >
              {t('workspace-info-preset-title')}
            </Typography>
            <Typography
              fontSize={16}
              fontWeight={300}
              gutterBottom
              variant="subtitle2"
            >
              {preset.title}
            </Typography>
          </Box>

          <Box sx={{ mb: 1.75 }}>
            <Typography
              fontSize={16}
              fontWeight={400}
              gutterBottom
              variant="subtitle2"
            >
              {t('workspace-info-short-description')}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {shortDescription || t('workspace-info-no-description')}
            </Typography>
          </Box>

          <Box sx={{ mb: 1 }}>
            <Box
              onClick={(): void => setIsAbstractOpen((prevState) => !prevState)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <Typography fontSize={16} fontWeight={400} variant="subtitle2">
                {t('workspace-info-abstract')}
              </Typography>
              {isAbstractOpen ? (
                <ChevronUp sx={{ mr: 15 }} />
              ) : (
                <ChevronDown sx={{ mr: 15 }} />
              )}
            </Box>
            <Collapse in={isAbstractOpen} unmountOnExit>
              <Typography
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
                variant="body2"
              >
                {abstractText || t('workspace-info-no-abstract')}
              </Typography>
            </Collapse>
          </Box>

          <Box sx={{ my: 1 }}>
            <Box
              onClick={(): void => setIsLabelsOpen((prevState) => !prevState)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <Typography fontSize={16} fontWeight={400} variant="subtitle2">
                {t('workspace-info-selected-labels', {
                  count: totalLabelCount,
                })}
              </Typography>
              {isLabelsOpen ? (
                <ChevronUp sx={{ mr: 15 }} />
              ) : (
                <ChevronDown sx={{ mr: 15 }} />
              )}
            </Box>
            <Collapse in={isLabelsOpen} unmountOnExit>
              {labelEntries.length === 0 && (
                <Typography
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                  variant="body2"
                >
                  {t('workspace-info-no-labels')}
                </Typography>
              )}
              {labelEntries.length > 0 && (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {labelEntries.map(([group, values]) => (
                    <Box key={group}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                        }}
                      >
                        {(values ?? []).map((value) => (
                          <Chip
                            color="secondary"
                            key={`${group}-${value}`}
                            label={labelFor(group, value)}
                            size="small"
                            sx={{
                              fontSize: '12px',
                              height: 24,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Collapse>
          </Box>

          <Box
            sx={{
              mt: 1.5,
              padding: 1.25,
              borderRadius: 1,
              border: '0.5px solid',
              borderColor: 'geowebColors.buttons.secondary.default.fill',
              backgroundColor:
                'geowebColors.typographyAndIcons.informationDialog',
            }}
          >
            <Stack spacing={0.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary" variant="caption">
                  {t('workspace-info-updated')}
                </Typography>
                <Typography color="text.secondary" variant="caption">
                  {t('workspace-info-author')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '13px',
                }}
              >
                <Typography variant="body2">
                  {formatDate(preset.updated) ??
                    t('workspace-info-no-date-provided')}
                </Typography>
                <Typography variant="body2">
                  {preset.updatedBy ?? t('workspace-info-unknown-author')}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.75 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary" variant="caption">
                  {t('workspace-info-created')}
                </Typography>
                <Typography color="text.secondary" variant="caption">
                  {t('workspace-info-created-by')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '13px',
                }}
              >
                <Typography variant="body2">
                  {formatDate(preset.date) ??
                    t('workspace-info-no-date-provided')}
                </Typography>
                <Typography
                  sx={{
                    maxWidth: '50%',
                  }}
                  variant="body2"
                >
                  {preset.username ?? t('workspace-info-unknown-author')}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default WorkspacePresetInfo;
